import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, Sparkles, ArrowLeft, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PostRequirementForm from '../components/PostRequirementForm';
import { AiSearchResultCard, useAiSearch } from '../components/ai/AiSearchResult';
import useSEO from '../lib/useSEO';

const AiSearchResultsPage = () => {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const city = params.get('city') || '';
  const [input, setInput] = useState(q);
  const [postOpen, setPostOpen] = useState(false);
  const { loading, error, result, run } = useAiSearch();

  useSEO({
    title: q ? `AI Search: ${q} — TelanganaMart` : 'AI Search — TelanganaMart',
    description: 'Ask TelanganaMart in natural language. Powered by Gemini AI.',
  });

  useEffect(() => {
    if (q) run(q, city);
  }, [q, city, run]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const next = new URLSearchParams(params);
    next.set('q', input.trim());
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0b1220]">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-600 dark:text-gray-400">
          <Link to="/" className="inline-flex items-center gap-1 hover:text-[#ee5e23] transition-colors" data-testid="ai-search-back">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <span>/</span>
          <span className="text-gray-900 dark:text-gray-100 font-medium">AI Search</span>
        </div>

        <div className="rounded-xl bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-700 p-4 md:p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-[#ee5e23]" />
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">AI Search</h1>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-50 text-[#ee5e23] border border-orange-200">Gemini</span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Type your requirement in natural language — we&apos;ll extract intent, city, and quantity, then match categories and vendors.
          </p>

          <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="ai-search-page-form">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. Who supplies GI pipes in Hyderabad?"
              className="flex-1 min-w-0 px-4 py-3 text-sm bg-white dark:bg-[#0b1220] border-2 border-orange-200 dark:border-orange-900/40 rounded-md focus:outline-none focus:border-[#ee5e23]"
              data-testid="ai-search-page-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#ee5e23] hover:bg-[#d24e15] text-white rounded-md text-sm font-semibold disabled:opacity-50"
              data-testid="ai-search-page-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </button>
          </form>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-4" data-testid="ai-search-page-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Understanding your query…
            </div>
          )}
          {error && (
            <div className="mt-4 text-sm text-red-600 dark:text-red-400" data-testid="ai-search-page-error">{error}</div>
          )}

          <AiSearchResultCard result={result} onPostAsRequirement={() => setPostOpen(true)} />
        </div>
      </div>

      <Footer />
      <PostRequirementForm open={postOpen} onOpenChange={setPostOpen} />
    </div>
  );
};

export default AiSearchResultsPage;
